/**
 * TenantBranding — full customisation surface for a tenant's visual
 * identity.  Works for both primary (system_admin) and white-label
 * tenants (owner / holding_admin / system_admin).
 *
 *   • Primary + accent colour pickers (with custom hex)
 *   • Logo upload + "auto-strip white background" toggle (+ live preview
 *     in both day & night modes)
 *   • Living-background preset picker (particles / starfield / aurora /
 *     mesh / video / none) — live preview that respects current theme
 *   • Custom video URL for the 'video' preset
 *   • Opacity slider for the living layer
 *
 * Everything saves to PATCH /api/tenants/me/branding and triggers a
 * BrandingProvider refresh so the new tokens propagate app-wide.
 */
import React, { useEffect, useState } from 'react';
import { Save, Palette, Image as ImageIcon, Video, Sparkles, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useBranding } from '@/context/BrandingContext';
import LivingBackground from '@/components/LivingBackground';
import { LogoMark } from '@/components/Logo';

const PRIMARY_PALETTE = [
  '#AE52EA', // Sovereign violet (default)
  '#D4AF37', // Bloomberg gold
  '#0EA5E9', // Calm sky
  '#10B981', // Emerald
  '#F43F5E', // Rose
  '#F59E0B', // Amber
  '#6366F1', // Indigo
  '#EC4899', // Magenta
];
const ACCENT_PALETTE = [
  null,        // = same as primary (no-op)
  '#FACC15',
  '#34D399',
  '#60A5FA',
  '#F472B6',
  '#A78BFA',
  '#FB7185',
  '#22D3EE',
];
const BG_PRESETS = [
  { id: 'particles', label: 'Particles', sub: 'Gold/violet network',     useFor: 'Venture studios, holdings — busy, connected.' },
  { id: 'starfield', label: 'Starfield', sub: 'Drifting stars · parallax', useFor: 'Sovereign wealth, futurists — wide, calm.' },
  { id: 'aurora',    label: 'Aurora',    sub: 'Soft colour wash',         useFor: 'Wealth managers, family offices — quiet, premium.' },
  { id: 'mesh',      label: 'Mesh',      sub: 'Moving radial gradient',   useFor: 'Fintech, SaaS — kinetic, modern.' },
  { id: 'video',     label: 'Custom video', sub: 'MP4/WebM URL you provide', useFor: 'Bespoke brands with their own motion identity.' },
  { id: 'none',      label: 'None',      sub: 'Static — no living layer', useFor: 'Conservative regulated tenants who want zero motion.' },
];

// ────────────────────────────────────────────────────────────────────
// Branding Marketplace — 6 hand-curated bundles operators can apply
// in one click. Each bundle is just a {primary, accent, bg_preset}
// triple — the operator can then still tweak any of those individual
// tokens afterwards.
// ────────────────────────────────────────────────────────────────────
const BRANDING_BUNDLES = [
  {
    id: 'sovereign-violet', name: 'Sovereign Violet', description: 'Default Amwāl OS — violet + chrome.',
    primary_color: '#AE52EA', accent_color: '#D4AF37', live_bg_preset: 'particles', bg_opacity: 0.6,
    swatches: ['#AE52EA', '#5E1F8B', '#D4AF37'],
  },
  {
    id: 'qatar-state', name: 'Qatar State', description: 'Maroon + gold — QNDS3 official accents.',
    primary_color: '#8A1538', accent_color: '#D4AF37', live_bg_preset: 'aurora', bg_opacity: 0.5,
    swatches: ['#8A1538', '#5E0E26', '#D4AF37'],
  },
  {
    id: 'midnight-emerald', name: 'Midnight Emerald', description: 'Deep green / black — sovereign wealth feel.',
    primary_color: '#10B981', accent_color: '#34D399', live_bg_preset: 'mesh', bg_opacity: 0.55,
    swatches: ['#10B981', '#064E3B', '#34D399'],
  },
  {
    id: 'palantir-graphite', name: 'Palantir Graphite', description: 'Stone-grey + electric blue — defense-grade.',
    primary_color: '#0EA5E9', accent_color: '#FACC15', live_bg_preset: 'starfield', bg_opacity: 0.7,
    swatches: ['#0EA5E9', '#1E293B', '#FACC15'],
  },
  {
    id: 'bloomberg-gold', name: 'Bloomberg Gold', description: 'Pure black + gold — terminal classic.',
    primary_color: '#D4AF37', accent_color: '#F59E0B', live_bg_preset: 'particles', bg_opacity: 0.5,
    swatches: ['#D4AF37', '#000000', '#F59E0B'],
  },
  {
    id: 'rose-incubator', name: 'Rose Incubator', description: 'Warm rose + magenta — founder-friendly.',
    primary_color: '#F43F5E', accent_color: '#EC4899', live_bg_preset: 'aurora', bg_opacity: 0.6,
    swatches: ['#F43F5E', '#9F1239', '#EC4899'],
  },
];

export default function TenantBranding() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({
    primary_color: '#AE52EA',
    accent_color: null,
    logo_url: '',
    logo_auto_transparent: true,
    live_bg_preset: 'particles',
    live_bg_video_url: '',
    live_bg_opacity: 0.6,
  });
  const [customHex, setCustomHex] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    if (!branding) return;
    // Sync local form state to the latest branding fetched by the
    // BrandingProvider. queueMicrotask defers the setState to satisfy
    // the react-hooks purity lint.
    queueMicrotask(() => setForm({
      primary_color: branding.primary_color || '#AE52EA',
      accent_color: branding.accent_color || null,
      logo_url: branding.logo_url || '',
      logo_auto_transparent: !!branding.logo_auto_transparent,
      live_bg_preset: branding.live_bg_preset || 'particles',
      live_bg_video_url: branding.live_bg_video_url || '',
      live_bg_opacity: typeof branding.live_bg_opacity === 'number' ? branding.live_bg_opacity : 0.6,
    }));
  }, [branding]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/tenants/me/branding', form);
      toast.success('Branding saved.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const applyBundle = async (bundle) => {
    setBusy(true);
    try {
      const patch = {
        primary_color: bundle.primary_color,
        accent_color: bundle.accent_color,
        live_bg_preset: bundle.live_bg_preset,
        live_bg_opacity: bundle.bg_opacity,
      };
      await api.patch('/tenants/me/branding', patch);
      queueMicrotask(() => setForm((p) => ({ ...p, ...patch })));
      await refresh();
      toast.success(`Applied "${bundle.name}". Hit Save again if you tweak anything else.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply bundle.');
    } finally { setBusy(false); }
  };

  /* Auto-pick — chooses the bundle that best matches this tenant's
     persona type + region heuristics. Pure client-side; the user can
     still override afterwards.
       - Qatar / KSA / GCC region                → "Qatar State"
       - Incubator / accelerator tenants         → "Rose Incubator"
       - FinTech / financial holdings            → "Bloomberg Gold"
       - HealthTech / Energy / sovereign-wealth  → "Midnight Emerald"
       - Defense / GovTech / dual-use            → "Palantir Graphite"
       - Anything else (the safe default)        → "Sovereign Violet" */
  const autoPickBundle = async () => {
    const blob = `${branding?.brand_name || ''} ${branding?.tenant_id || ''} ${form.brand_name || ''}`.toLowerCase();
    let pick = BRANDING_BUNDLES[0]; // sovereign-violet default
    if (/qatar|qfc|qfcra|nds|qfma|moci|gcc|ksa|saudi|uae|emirate/.test(blob)) pick = BRANDING_BUNDLES.find((b) => b.id === 'qatar-state');
    else if (/incubator|accelerator|cohort|garage|alpha/.test(blob))           pick = BRANDING_BUNDLES.find((b) => b.id === 'rose-incubator');
    else if (/bank|finance|fintech|capital|wealth|fund|invest/.test(blob))     pick = BRANDING_BUNDLES.find((b) => b.id === 'bloomberg-gold');
    else if (/health|energy|sovereign|swf|pension/.test(blob))                 pick = BRANDING_BUNDLES.find((b) => b.id === 'midnight-emerald');
    else if (/defense|gov|tech|cyber|intel|palantir/.test(blob))               pick = BRANDING_BUNDLES.find((b) => b.id === 'palantir-graphite');
    await applyBundle(pick || BRANDING_BUNDLES[0]);
  };

  const upload = async (f) => {
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error('Logo must be ≤ 4 MB.'); return; }
    const fd = new FormData();
    fd.append('file', f);
    fd.append('auto_transparent', String(form.logo_auto_transparent !== false));
    setBusy(true);
    try {
      // Dedicated tenant-logo endpoint — runs Pillow background-removal
      // and stores a data URL on the tenant doc so the new logo is
      // visible on EVERY surface (incl. unauthenticated marketing
      // pages) immediately on next page load.
      const { data } = await api.post('/tenants/me/branding/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data?.url) throw new Error('Upload returned no URL.');
      setForm((p) => ({ ...p, logo_url: data.url, logo_auto_transparent: true }));
      // Refresh BrandingContext so the navbar logo updates instantly.
      await refresh();
      toast.success(`Logo uploaded (${data.mode === 'svg-passthrough' ? 'SVG' : 'transparent PNG'}). Click "Save" to lock in any other branding changes.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="tenant-branding-page">
      <div className="ve-card p-6 flex items-start gap-4">
        <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)' }}>
          <Palette size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Customisation · Tenant Branding</div>
          <h1 className="display text-2xl mt-1">Make this workspace yours.</h1>
          <p className="text-sm opacity-70 mt-1 max-w-2xl">
            Pick your brand colours, upload a logo, and choose the living background that runs across the app at night.
            Day mode keeps things calm — the animated layer hides automatically.
          </p>
        </div>
        <button onClick={save} disabled={busy} data-testid="branding-save"
          className="ve-btn-primary inline-flex items-center gap-1.5 self-start">
          <Save size={14}/> {busy ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* ── Branding Marketplace — 6 curated bundles, one-click apply ── */}
      <div className="ve-card p-6" data-testid="branding-marketplace">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div>
            <div className="ve-eyebrow">[00] Branding Marketplace</div>
            <h2 className="display text-lg mt-1">Pick a vibe. Customise later.</h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-[11px] opacity-65 max-w-md">
              Each bundle sets your primary + accent colour and a matching living background. You can fine-tune any individual token below afterwards.
            </p>
            <button
              type="button"
              onClick={autoPickBundle}
              disabled={busy}
              data-testid="branding-auto-pick"
              className="px-3 py-2 rounded text-[10px] uppercase tracking-[0.22em] font-bold border border-[var(--ve-violet)] text-[var(--ve-violet)] dark:text-[var(--ve-violet-bright)] hover:bg-[var(--ve-violet)]/10 transition-colors inline-flex items-center gap-1.5 shrink-0"
              title="Picks the bundle that best matches your persona type + region"
            >
              ✨ Auto-pick for me
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {BRANDING_BUNDLES.map((b) => {
            const active = form.primary_color === b.primary_color && form.live_bg_preset === b.live_bg_preset;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => applyBundle(b)}
                disabled={busy}
                data-testid={`branding-bundle-${b.id}`}
                className={`text-left rounded-lg p-4 border transition-all relative overflow-hidden group ${
                  active
                    ? 'border-[var(--brand-primary)] ring-2 ring-[var(--brand-primary)]/40'
                    : 'border-white/15 dark:border-white/15 border-slate-200 hover:border-[var(--brand-primary)]'
                }`}
                style={{ background: `linear-gradient(135deg, ${b.swatches[1]}22 0%, ${b.swatches[0]}11 100%)` }}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  {b.swatches.map((sw) => (
                    <span key={sw} className="h-4 w-4 rounded-full border border-white/30" style={{ background: sw }} />
                  ))}
                  {active && (
                    <span className="ml-auto text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded">
                      Active
                    </span>
                  )}
                </div>
                <div className="display text-sm font-bold">{b.name}</div>
                <div className="text-[11px] opacity-65 mt-1 leading-relaxed">{b.description}</div>
                <div className="text-[9px] uppercase tracking-[0.18em] opacity-50 mt-2">
                  bg · {b.live_bg_preset}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Colours ── */}
      <div className="ve-card p-6">
        <div className="ve-eyebrow mb-4">[01] Brand colours</div>

        <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-65 mb-2">Primary</div>
        <div className="flex flex-wrap gap-2 items-center">
          {PRIMARY_PALETTE.map((c) => (
            <button key={c} type="button" data-testid={`brand-primary-${c.slice(1)}`}
              onClick={() => setForm({ ...form, primary_color: c })}
              className={`h-10 w-10 rounded-full border-2 transition-transform ${form.primary_color === c ? 'border-white scale-110 ring-2 ring-offset-2 ring-[var(--brand-primary)]' : 'border-white/40 hover:scale-105'}`}
              style={{ background: c }} aria-label={c}/>
          ))}
          <div className="flex items-center gap-2 ml-2 pl-2 border-l border-white/10">
            <input type="color" value={form.primary_color}
              onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
              data-testid="brand-primary-custom"
              className="h-10 w-12 rounded cursor-pointer bg-transparent border border-white/20"/>
            <input type="text" placeholder="#AE52EA" value={customHex}
              onChange={(e) => setCustomHex(e.target.value)}
              onBlur={() => {
                if (/^#[0-9A-Fa-f]{6}$/.test(customHex)) { setForm({ ...form, primary_color: customHex }); setCustomHex(''); }
              }}
              className="w-28 px-2 py-2 text-xs pal-mono rounded border border-white/15 bg-transparent focus:outline-none focus:border-[var(--brand-primary)]"/>
          </div>
        </div>

        <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-65 mt-6 mb-2">Accent (highlights, glows)</div>
        <div className="flex flex-wrap gap-2">
          {ACCENT_PALETTE.map((c, i) => (
            <button key={c || `none-${i}`} type="button" data-testid={`brand-accent-${c ? c.slice(1) : 'none'}`}
              onClick={() => setForm({ ...form, accent_color: c })}
              className={`h-9 w-9 rounded-full border-2 transition-transform ${form.accent_color === c ? 'border-white scale-110 ring-2 ring-offset-2 ring-[var(--brand-accent)]' : 'border-white/40 hover:scale-105'} flex items-center justify-center`}
              style={c ? { background: c } : { background: 'transparent' }}>
              {!c && <span className="text-[8px] opacity-50">=PRI</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── Logo ── */}
      <div className="ve-card p-6">
        <div className="ve-eyebrow mb-4">[02] Logo & wordmark</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div
              onClick={() => fileRef.current?.click()}
              data-testid="branding-logo-uploader"
              className="rounded-lg border-2 border-dashed border-white/20 hover:border-[var(--brand-primary)] cursor-pointer p-6 text-center transition-colors"
            >
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="h-20 w-auto max-w-full mx-auto object-contain"/>
              ) : (
                <>
                  <ImageIcon size={32} className="mx-auto opacity-40"/>
                  <p className="mt-3 text-sm opacity-70">Click to upload (PNG / JPG / SVG, ≤ 4 MB)</p>
                </>
              )}
              <input
                ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="branding-logo-file"
              />
            </div>
            <label className="mt-4 flex items-start gap-3 cursor-pointer p-3 rounded border border-white/10 hover:bg-white/5">
              <input type="checkbox" checked={form.logo_auto_transparent}
                onChange={(e) => setForm({ ...form, logo_auto_transparent: e.target.checked })}
                data-testid="branding-logo-transparent"
                className="mt-1"/>
              <div>
                <div className="text-sm font-bold flex items-center gap-1.5">
                  {form.logo_auto_transparent ? <EyeOff size={13}/> : <Eye size={13}/>}
                  Auto-transparent background
                </div>
                <div className="text-[11px] opacity-65 mt-0.5 leading-relaxed">
                  Drops any white background out of your uploaded asset using blend-mode. Adds a subtle animated halo behind the mark in your primary colour.
                </div>
              </div>
            </label>
          </div>

          {/* Live preview in DAY + NIGHT side-by-side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 p-4 bg-white">
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Day preview</div>
              <div className={`flex items-center gap-3 ${form.logo_auto_transparent ? 've-day-preview' : ''}`}>
                {form.logo_url
                  ? <img src={form.logo_url} className={`h-10 w-10 object-contain ${form.logo_auto_transparent ? 've-auto-transparent-logo' : ''}`}/>
                  : <LogoMark className="h-10 w-10"/>}
                <span style={{ color: form.primary_color }} className="font-serif font-bold tracking-wider uppercase text-sm">
                  {branding?.brand_name || 'Your Brand'}
                </span>
              </div>
            </div>
            <div className="rounded-lg border border-white/10 p-4 bg-[#0A0A0A]">
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/40 mb-3">Night preview</div>
              <div className={`flex items-center gap-3 ${form.logo_auto_transparent ? 've-night-preview' : ''}`}>
                {form.logo_url
                  ? <img src={form.logo_url} className={`h-10 w-10 object-contain ${form.logo_auto_transparent ? 've-auto-transparent-logo' : ''}`}/>
                  : <LogoMark className="h-10 w-10"/>}
                <span style={{ color: form.primary_color }} className="font-serif font-bold tracking-wider uppercase text-sm">
                  {branding?.brand_name || 'Your Brand'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Living background ── */}
      <div className="ve-card p-6 relative overflow-hidden">
        <div className="ve-eyebrow mb-4 relative z-10">
          [03] Living background <span className="opacity-60">· renders in both day & night</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 relative z-10">
          {BG_PRESETS.map((p) => (
            <button key={p.id} type="button"
              onClick={() => setForm({ ...form, live_bg_preset: p.id })}
              data-testid={`branding-bg-${p.id}`}
              className={`text-left p-3 rounded border transition-colors ${form.live_bg_preset === p.id ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]' : 'border-white/10 hover:border-white/30'}`}>
              <div className="text-[12px] font-bold flex items-center gap-1.5">
                {p.id === 'video' ? <Video size={11}/> : <Sparkles size={11}/>}
                {p.label}
              </div>
              <div className="text-[10px] opacity-60 mt-0.5 leading-snug">{p.sub}</div>
              <div className="text-[10px] opacity-50 mt-1 italic leading-snug">
                {p.useFor}
              </div>
            </button>
          ))}
        </div>

        {form.live_bg_preset === 'video' && (
          <div className="mt-4 relative z-10">
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-65 block mb-1">Custom video URL (mp4 / webm, looping)</label>
            <input type="url" value={form.live_bg_video_url || ''}
              onChange={(e) => setForm({ ...form, live_bg_video_url: e.target.value })}
              placeholder="https://your-cdn.example.com/loop.mp4"
              data-testid="branding-bg-video-url"
              className="w-full rounded border border-white/15 bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-primary)]"/>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 relative z-10">
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-65">Intensity</label>
          <input type="range" min="0.1" max="1.0" step="0.05"
            value={form.live_bg_opacity}
            onChange={(e) => setForm({ ...form, live_bg_opacity: parseFloat(e.target.value) })}
            data-testid="branding-bg-opacity"
            className="flex-1 max-w-xs accent-[var(--brand-primary)]"/>
          <span className="pal-mono text-[11px] opacity-70 w-12 text-right">{Math.round(form.live_bg_opacity * 100)}%</span>
        </div>

        {/* In-page preview frame — renders the selected preset locally
            inside a dark card so the day/light theme of the rest of the
            page doesn't suppress it. */}
        <div className="mt-5 relative h-44 rounded-lg overflow-hidden border border-white/15 bg-[#0A0A0A]">
          <div className="absolute inset-0 ve-bg-preview-frame">
            {/* Force-render inside this dark frame */}
            <LivingBackgroundPreview
              preset={form.live_bg_preset}
              color={form.primary_color}
              accent={form.accent_color}
              opacity={form.live_bg_opacity}
              videoUrl={form.live_bg_video_url}
            />
          </div>
          <div className="relative h-full flex items-center justify-center text-center px-6">
            <div>
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/40">Live preview</div>
              <div className="display text-xl mt-1 text-white">{branding?.brand_name || 'Your Brand'}</div>
              <div className="text-[10px] opacity-60 mt-1">Renders behind every page — both day and night themes (palettes auto-adapt).</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={busy} data-testid="branding-save-2"
          className="ve-btn-primary inline-flex items-center gap-1.5">
          <Save size={14}/> {busy ? 'Saving…' : 'Save branding'}
        </button>
      </div>
    </div>
  );
}

/**
 * In-frame preview renderer — runs the same animations as
 * <LivingBackground /> but contained inside a fixed-height card, and
 * always renders (ignoring day/night) so the operator can preview night
 * mode while the rest of the app is in day mode.
 */
function LivingBackgroundPreview({ preset, color, accent, opacity, videoUrl }) {
  if (preset === 'none') return null;
  if (preset === 'video') {
    return videoUrl ? (
      <video src={videoUrl} autoPlay muted loop playsInline
        className="w-full h-full object-cover" style={{ opacity }}/>
    ) : <div className="absolute inset-0 flex items-center justify-center text-[10px] opacity-50">Paste a video URL above</div>;
  }
  if (preset === 'aurora' || preset === 'mesh') {
    return <div className={preset === 'aurora' ? 've-aurora' : 've-mesh'}
      style={{ position: 'absolute', inset: 0, opacity, '--bg-c1': color, '--bg-c2': accent || color }}/>;
  }
  // particles / starfield — use a mini canvas
  return <MiniCanvas preset={preset} color={color} opacity={opacity}/>;
}

function MiniCanvas({ preset, color, opacity }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    let raf;
    const resize = () => { c.width = c.parentElement.clientWidth; c.height = c.parentElement.clientHeight; };
    resize();
    const obs = new ResizeObserver(resize); obs.observe(c.parentElement);
    if (preset === 'starfield') {
      const stars = Array.from({ length: 80 }, () => ({ x: Math.random()*c.width, y: Math.random()*c.height, z: Math.random()+0.2, tw: Math.random()*Math.PI*2 }));
      const loop = () => {
        ctx.clearRect(0,0,c.width,c.height);
        stars.forEach((s) => {
          s.tw += 0.04; const a = (Math.sin(s.tw)*0.4+0.6)*s.z;
          ctx.fillStyle = color; ctx.globalAlpha = a; ctx.beginPath();
          ctx.arc(s.x, s.y, s.z*1.4, 0, Math.PI*2); ctx.fill();
          s.y += s.z*0.2; if (s.y>c.height){ s.y=0; s.x=Math.random()*c.width; }
        });
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(loop);
      };
      loop();
    } else {
      const parts = Array.from({ length: 36 }, () => ({ x: Math.random()*c.width, y: Math.random()*c.height, vx: (Math.random()-0.5)*0.5, vy: (Math.random()-0.5)*0.5, r: Math.random()*1.4+0.4 }));
      const loop = () => {
        ctx.clearRect(0,0,c.width,c.height);
        ctx.fillStyle = color;
        parts.forEach((p) => { p.x+=p.vx; p.y+=p.vy; if (p.x<0||p.x>c.width) p.vx*=-1; if (p.y<0||p.y>c.height) p.vy*=-1; ctx.beginPath(); ctx.arc(p.x,p.y,p.r,0,Math.PI*2); ctx.fill(); });
        ctx.strokeStyle = color; ctx.lineWidth = 0.4;
        for (let i=0;i<parts.length;i++) for (let j=i+1;j<parts.length;j++) { const a=parts[i],b=parts[j]; const d=Math.hypot(a.x-b.x,a.y-b.y); if (d<90) { ctx.globalAlpha = (1-d/90)*0.5; ctx.beginPath(); ctx.moveTo(a.x,a.y); ctx.lineTo(b.x,b.y); ctx.stroke(); } }
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(loop);
      };
      loop();
    }
    return () => { cancelAnimationFrame(raf); obs.disconnect(); };
  }, [preset, color]);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" style={{ opacity }}/>;
}
